/**
 * Shared helpers used by every 2D DICOM viewport that supports segmentation
 * painting (DicomVisualization and OrthogonalCprVisualization).
 *
 * Keep this module intentionally small: only logic that is genuinely identical
 * across viewports belongs here.  Viewport-specific paint-widget wiring (mouse
 * events, reslice-vs-volume handling) stays in each viewport.
 */

import vtkColorTransferFunction from 'vtk.js/Sources/Rendering/Core/ColorTransferFunction';
import { getTopLevelDUName } from './Common';

/**
 * Corresponds to the SegmentationColor DU on the F# side.
 */
export const SegmentationColor = Object.freeze({
    Red: 'Red',
    Blue: 'Blue',
});

const redTransferFunction = vtkColorTransferFunction.newInstance();
redTransferFunction.addRGBPoint(1, 1, 0, 0);
redTransferFunction.addRGBPoint(2, 1, 1, 0); // value 2 = yellow (edited region, axial only)
const blueTransferFunction = vtkColorTransferFunction.newInstance();
blueTransferFunction.addRGBPoint(1, 0, 0, 1);
blueTransferFunction.addRGBPoint(2, 1, 1, 0); // value 2 = yellow (edited region, axial only)

export function getTransferFunctionByColor(color) {
    switch (getTopLevelDUName(color)) {
        case SegmentationColor.Red: return redTransferFunction;
        case SegmentationColor.Blue: return blueTransferFunction;
        default: return redTransferFunction;
    }
}

/**
 * Convert a user-facing brush size (in voxels) into the radius that
 * vtkPaintFilter / vtkPaintWidget expect.  The -0.1 nudge compensates for the
 * painter treating the radius inclusively, producing a brush that visually
 * matches the requested size.
 */
export function brushSizeToRadius(brushSize) {
    return brushSize / 2.0 - 0.1;
}

/**
 * Clamp a painting Z-coordinate slightly inside the volume bounds.
 *
 * When the slice location sits exactly on the outer face of the volume, the
 * world-to-index transform rounds to nx (one past the last valid voxel).
 * Nudging by epsilon keeps it on the last valid slice.
 */
export function getCorrectedPaintZ(sliceLocation, minBound, maxBound) {
    const epsilon = 0.001;
    if (sliceLocation >= maxBound) {
        return maxBound - epsilon;
    }
    if (sliceLocation <= minBound) {
        return minBound + epsilon;
    }
    return sliceLocation;
}

/**
 * On boundary slices VTK's SVG-rendered brush outline can be clipped away by
 * the view frustum.  Nudge the manipulator half a voxel inside the volume so
 * the outline stays visible while the actual painted slice is unchanged.
 *
 * Returns the adjusted Z or null when no adjustment is needed.
 */
export function getManipulatorAdjustment(sliceLocation, minBound, maxBound, spacing) {
    const halfSpacing = spacing * 0.5;

    if (maxBound - sliceLocation < halfSpacing) {
        return maxBound - halfSpacing;
    }
    if (sliceLocation - minBound < halfSpacing) {
        return minBound + halfSpacing;
    }
    return null;
}

/**
 * Extract the painted slice buffer for the given slice index from the paint
 * filter's output.  Returned shape matches SegmentationSliceData on the F# side.
 */
export function extractPaintedSlice(painterOutputData, sliceIdx) {
    const [nx, ny] = painterOutputData.getDimensions();
    const scalars = painterOutputData.getPointData().getScalars().getData();
    const sliceSize = nx * ny;
    const offset = sliceIdx * sliceSize;
    return {
        buffer: scalars.subarray(offset, offset + sliceSize),
        dimensions: [nx, ny, 1],
        origin: painterOutputData.getOrigin(),
        spacing: painterOutputData.getSpacing(),
    };
}
